// Tracks barcodes scanned on the scansheet within a short TTL so the
// same barcode being re-scanned doesn't award gamification points.
// Persisted to localStorage so the suppression survives a refresh /
// brief navigate-away — the operator can't farm points by reloading.

const STORAGE_KEY = "scansheet-recent-scans";
const TTL_MS = 2 * 60 * 1000;

type RecentMap = Record<string, number>;

const read = (): RecentMap => {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
        return {};
    }
};

const write = (map: RecentMap) => {
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
    } catch {
        // localStorage unavailable — dedup silently degrades to no-op.
    }
};

const prune = (map: RecentMap, now: number): RecentMap => {
    const next: RecentMap = {};
    for (const [barcode, ts] of Object.entries(map)) {
        if (typeof ts === "number" && now - ts < TTL_MS) next[barcode] = ts;
    }
    return next;
};

export const wasRecentlyScanned = (barcode: string): boolean => {
    const ts = read()[barcode];
    return typeof ts === "number" && Date.now() - ts < TTL_MS;
};

export const markScanned = (barcode: string) => {
    const now = Date.now();
    const map = prune(read(), now);
    map[barcode] = now;
    write(map);
};

// A tote scan clears the whole window. On the BargainFox sheet an operator
// working through identical units scans the same FNSKU once per unit, each
// separated by the tote scan that commits the previous one — that's real work,
// not point farming, so anything scanned before the tote is fair game again.
// Only a repeat with no tote scan in between still counts as a duplicate.
export const clearRecentScans = () => {
    try {
        window.localStorage.removeItem(STORAGE_KEY);
    } catch {
        // localStorage unavailable — dedup silently degrades to no-op.
    }
};
